import { uniqueName } from '@reptree/shared'

/**
 * The rules behind RepTree's passkey second step, with no browser or SDK dependency so they are unit-tested in
 * Node (test/passkey-client.test.mjs): which host can run a ceremony, how a device is named, the WebAuthn options
 * RepTree sends, how the server's JSON and the browser's answers are converted, when a prefetched challenge is still
 * good, and how a failure is explained. lib/passkey.ts wires them to the RepTree API and navigator.credentials.
 */

export interface PasskeyEnv {
  /** A passkey ceremony can run here (WebAuthn exists, secure page, the configured relying-party host). */
  usable: boolean
  /** This device has its own fingerprint / face / screen-lock authenticator (Touch ID, Face ID, Windows Hello). */
  platform: boolean
}

/** The browser only honors the relying-party id on that exact host (rp.id reptree.io fails on onrender, localhost
 *  and agency domains with a SecurityError), so passkeys are offered only there. www 301s to the apex. */
export const passkeyHostOk = (hostname: string, rpId: string): boolean => hostname === rpId

/** A plain device name for the passkey list, from the user agent. iPadOS reports a desktop Mac user agent, so a
 *  Mac that has touch points is an iPad. */
export function deviceLabel(ua: string, maxTouchPoints = 0): string {
  if (/iPhone|iPod/i.test(ua)) return 'iPhone'
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1)) return 'iPad'
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'Android phone' : 'Android tablet'
  if (/CrOS/i.test(ua)) return 'Chromebook'
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac'
  if (/Windows/i.test(ua)) return 'Windows computer'
  if (/Linux/i.test(ua)) return 'computer'
  return 'this device'
}

/** The device a new passkey is listed under ("Passkey on iPhone"). Without a built-in authenticator the passkey is
 *  made on the person's phone (QR) or a security key, so it is not named after this computer. */
export const passkeyLabel = (ua: string, maxTouchPoints: number, platform: boolean): string =>
  platform ? deviceLabel(ua, maxTouchPoints) : 'your phone or key'

/** A phone or tablet (the device people carry), as opposed to a computer. */
export const isHandheld = (label: string): boolean => /iPhone|iPad|Android/.test(label)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A name for a new authenticator-app setup, unique among the account's factors: Supabase refuses a duplicate name
 * across ALL factors (unverified ones and the passkey bridge included), and a unique name means an unfinished setup
 * never needs removing, which would email "a method was removed" for something the person only cancelled.
 */
export const appName = (existing: readonly string[], date: Date): string =>
  uniqueName(`Authenticator app (${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()})`, existing)

// ---- base64url and the JSON the RepTree API speaks (the same shapes as PublicKeyCredential.toJSON()).

export function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4))
  const out = new Uint8Array(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function bytesToB64url(b: ArrayBuffer | ArrayBufferView): string {
  const u = b instanceof ArrayBuffer ? new Uint8Array(b) : new Uint8Array(b.buffer, b.byteOffset, b.byteLength)
  let bin = ''
  for (const x of u) bin += String.fromCharCode(x)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

type CredentialRef = { type: 'public-key'; id: string; transports?: string[] }

/** The options for adding a passkey, as the server sends them (binary values in base64url). */
export interface CreationJSON {
  rp: { id: string; name: string }
  user: { id: string; name: string; displayName: string }
  challenge: string
  pubKeyCredParams: { type: 'public-key'; alg: number }[]
  timeout?: number
  excludeCredentials?: CredentialRef[]
}

/** The options for confirming with a passkey, as the server sends them. */
export interface RequestJSON {
  challenge: string
  rpId: string
  timeout?: number
  userVerification?: UserVerificationRequirement
  allowCredentials?: CredentialRef[]
}

type CreationWithHints = PublicKeyCredentialCreationOptions & { hints?: string[] }
type RequestWithHints = PublicKeyCredentialRequestOptions & { hints?: string[] }

/**
 * The options for creating the passkey: the server's (challenge, rp, user, algorithms, the passkeys to exclude) plus
 * this device's choices. With a built-in authenticator: use it (Face ID, Touch ID, Windows Hello), and keep a
 * discoverable (synced) credential when the platform offers one, so a replaced phone restores it from iCloud Keychain
 * or Google Password Manager. Without one: let the browser offer a phone (QR) or a security key. Existing passkeys are
 * excluded WITHOUT transports, so every authenticator checks the list and a device never saves a second passkey over
 * its own. `timeoutMs` keeps the prompt inside the challenge's remaining life.
 */
export function creationOptions(server: CreationJSON, { platform, email, timeoutMs }: { platform: boolean; email: string; timeoutMs?: number }): CreationWithHints {
  const name = server.user.name || email
  return {
    rp: server.rp,
    user: { id: b64urlToBytes(server.user.id), name, displayName: server.user.displayName || name },
    challenge: b64urlToBytes(server.challenge),
    pubKeyCredParams: server.pubKeyCredParams,
    excludeCredentials: (server.excludeCredentials ?? []).map((c) => ({ type: 'public-key' as const, id: b64urlToBytes(c.id) })),
    authenticatorSelection: {
      ...(platform ? { authenticatorAttachment: 'platform' as const } : {}),
      residentKey: 'preferred',
      requireResidentKey: false,
      userVerification: 'preferred',
    },
    attestation: 'none',
    hints: platform ? ['client-device'] : ['hybrid'],
    timeout: timeoutMs ?? server.timeout,
  }
}

/** The options for confirming with a passkey: the server's (challenge, rp id, the account's passkeys and where they
 *  live), with no hint that would steer the prompt toward a hardware key. */
export function requestOptions(server: RequestJSON, timeoutMs?: number): RequestWithHints {
  return {
    challenge: b64urlToBytes(server.challenge),
    rpId: server.rpId,
    userVerification: server.userVerification ?? 'preferred',
    allowCredentials: (server.allowCredentials ?? []).map((c) => ({
      type: 'public-key' as const,
      id: b64urlToBytes(c.id),
      ...(c.transports?.length ? { transports: c.transports as AuthenticatorTransport[] } : {}),
    })),
    hints: [],
    timeout: timeoutMs ?? server.timeout,
  }
}

/** A browser answer, shaped like a PublicKeyCredential (only what is read here). */
interface CredentialLike {
  id: string
  rawId: ArrayBuffer
  type: string
  response: {
    clientDataJSON: ArrayBuffer
    attestationObject?: ArrayBuffer
    getTransports?: () => string[]
    authenticatorData?: ArrayBuffer
    signature?: ArrayBuffer
    userHandle?: ArrayBuffer | null
  }
}

/** A new passkey as the server takes it (PublicKeyCredential.toJSON() for a registration, without the extras). */
export function registrationJSON(c: CredentialLike) {
  const r = c.response
  let transports: string[] = []
  try {
    transports = typeof r.getTransports === 'function' ? r.getTransports() : []
  } catch {
    transports = []
  }
  return {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    response: { clientDataJSON: bytesToB64url(r.clientDataJSON), attestationObject: bytesToB64url(r.attestationObject!), transports },
  }
}

/** A confirmation as the server takes it. */
export function assertionJSON(c: CredentialLike) {
  const r = c.response
  return {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      authenticatorData: bytesToB64url(r.authenticatorData!),
      signature: bytesToB64url(r.signature!),
      // Some authenticators report an empty handle: that is no handle.
      userHandle: r.userHandle && r.userHandle.byteLength > 0 ? bytesToB64url(r.userHandle) : null,
    },
  }
}

// ---- Challenge freshness.

/** The server expires a challenge 300 s after issuing it. */
export const CHALLENGE_LIFE_MS = 300_000
/** How old a prefetched challenge may be when the person taps. Older than this (a phone that locked with the panel
 *  open, a throttled background tab), the tap fetches a fresh one first instead of letting the prompt succeed and
 *  the server then refuse it. Kept short so that even the slow route (a QR code scanned with a phone over Bluetooth)
 *  still has minutes left before the 300 s limit. Measured from arrival on this device, so a wrong device clock does
 *  not matter. */
export const CHALLENGE_TAP_MAX_AGE_MS = 90_000
/** A prefetched challenge is renewed this often while it waits for a tap. */
export const CHALLENGE_RENEW_MS = 60_000
export const challengeFresh = (fetchedAtMs: number, nowMs: number): boolean => nowMs - fetchedAtMs < CHALLENGE_TAP_MAX_AGE_MS
/** The prompt's own time limit: whatever is left of the challenge, less a margin for the round trip. */
export const promptTimeoutMs = (fetchedAtMs: number, nowMs: number): number => Math.max(30_000, CHALLENGE_LIFE_MS - (nowMs - fetchedAtMs) - 20_000)

// ---- Failures in plain words.

export interface PasskeyProblem {
  /** Plain words for the person. Never the browser's or server's own text. */
  message: string
  /** Fetch a new challenge before the next try (expired, or the challenge was used). */
  rechallenge: boolean
  /** Offering the other method helps (passkeys unavailable here, or not switched on yet). */
  offerOther: boolean
  /** This device's session, or the passkey, no longer exists on the server: resync (lib/api resyncSession). */
  resync?: boolean
  /** This session must confirm it is the person (a second step) before the action can be retried. */
  stepUp?: boolean
  /** Nothing to say: a second tap while the first prompt is still open. */
  silent?: boolean
}

const TRY_AGAIN: PasskeyProblem = { message: 'That did not go through. Try again.', rechallenge: true, offerOther: false }

/** Plain-language meaning of a failed sign-in-method step, from a DOMException (the browser prompt), a RepTree API
 *  error (status, and details.code from server lib/passkeys), or a Supabase Auth error (its code) in the
 *  authenticator-app setup and removals. */
export function passkeyProblem(e: unknown, rpId: string): PasskeyProblem {
  const err = (e ?? {}) as { name?: string; code?: unknown; status?: number; message?: string; details?: { code?: string } | null }
  const name = err.name ?? ''
  const code = err.details?.code ?? (typeof err.code === 'string' ? err.code : '')
  // A tap while the device prompt is already open: the first one carries on.
  if (name === 'OperationError') return { message: '', rechallenge: false, offerOther: false, silent: true }
  // The browser prompt. NotAllowedError covers cancel, time out, no passkey on this device, and a lost focus alike,
  // so it never blames the person or the passkey.
  if (name === 'NotAllowedError' || name === 'AbortError')
    return { message: 'That did not go through. Try again when you are ready.', rechallenge: true, offerOther: false }
  if (name === 'InvalidStateError' || code === 'already_registered')
    return { message: 'This device already has a RepTree passkey. You can use that one to sign in.', rechallenge: true, offerOther: false }
  if (name === 'SecurityError' || code === 'wrong_origin' || code === 'wrong_rp')
    return { message: `Passkeys work only at ${rpId}. Open it there.`, rechallenge: false, offerOther: true }
  if (name === 'NotSupportedError')
    return { message: 'This browser cannot use passkeys. Open RepTree in Safari, Chrome or Edge.', rechallenge: false, offerOther: true }
  // This device's session is gone (the RepTree API's 401, or Supabase's own answer).
  if (err.status === 401 || name === 'AuthSessionMissingError' || code === 'session_not_found')
    return { message: 'You were signed out on this device. Please sign in again.', rechallenge: false, offerOther: false, resync: true }
  if (code === 'challenge_expired' || code === 'challenge_mismatch') return { message: 'That took a little too long. Try again.', rechallenge: true, offerOther: false }
  if (code === 'busy') return { message: 'One moment, then try again.', rechallenge: true, offerOther: false }
  if (code === 'sign_in_with_password')
    return { message: 'Sign out, then sign in with your email and password and confirm with your passkey.', rechallenge: false, offerOther: false }
  if (code === 'no_passkey' || code === 'mfa_factor_not_found')
    return { message: 'Your sign-in methods changed. One moment while this page catches up.', rechallenge: false, offerOther: false, resync: true }
  if (code === 'unknown_passkey')
    return { message: 'That passkey is for a different account. Choose the RepTree passkey for this account.', rechallenge: true, offerOther: true }
  if (code === 'setup_off') return { message: 'Passkeys are not switched on yet. Use an authenticator app for now.', rechallenge: false, offerOther: true }
  if (code === 'counter')
    return { message: 'This passkey could not be trusted. Use another way to confirm, or email hello@reptree.io.', rechallenge: true, offerOther: true }
  if (code === 'insufficient_aal') return { message: 'Please confirm it is you first, then try again.', rechallenge: false, offerOther: false, stepUp: true }
  if (code === 'too_many_enrolled_mfa_factors')
    return { message: 'This account has too many sign-in methods. Remove one you no longer use, then try again.', rechallenge: false, offerOther: false }
  // The server's own words for these are already plain (bridge_missing, too_many), and so is a 429 or a 503.
  if (code === 'bridge_missing' || code === 'too_many' || err.status === 429 || err.status === 503)
    return { message: err.message || TRY_AGAIN.message, rechallenge: code !== 'too_many', offerOther: code === 'bridge_missing' }
  return TRY_AGAIN
}
